import { z } from "zod";

/** Where this schema's data is allowed to flow. */
export type DomainSurface = "store" | "pxpf" | "ai" | "ui";

/** Owning subsystem; used to group MCP tools and docs. */
export type DomainGroup = "canvas" | "playback" | "project" | "tools" | "fsm" | "pxpf";

/** Strongly-typed metadata payload for every registered schema. */
export interface DomainMeta {
  /** Stable kebab/snake identifier; used as JSON-Schema $id. */
  id: string;
  title: string;
  description: string;
  surface: DomainSurface;
  /** Expose to MCP/LLM tool surface. */
  llmExposed: boolean;
  /** If set, this field is autosave-eligible under this key. */
  persistKey?: string;
  examples?: readonly unknown[];
  group?: DomainGroup;
  deprecated?: string;
}

/**
 * Project-wide registry. The strongly-typed metadata makes every
 * `.register(domainRegistry, …)` call a compile-time check.
 */
export const domainRegistry = z.registry<DomainMeta, z.ZodType>();

const knownSchemas = new Set<z.ZodType>();
const _origAdd = domainRegistry.add.bind(domainRegistry);
domainRegistry.add = (schema, meta) => {
  knownSchemas.add(schema);
  return _origAdd(schema, meta);
};

export function* registered(): Iterable<readonly [z.ZodType, DomainMeta]> {
  for (const schema of knownSchemas) {
    const meta = domainRegistry.get(schema);
    if (meta) yield [schema, meta] as const;
  }
}

export function listLlmExposed(): readonly DomainMeta[] {
  const out: DomainMeta[] = [];
  for (const [, meta] of registered()) if (meta.llmExposed) out.push(meta);
  return out;
}

export function listPersistable(): readonly { key: string; meta: DomainMeta; schema: z.ZodType }[] {
  const out: { key: string; meta: DomainMeta; schema: z.ZodType }[] = [];
  for (const [schema, meta] of registered()) {
    if (meta.persistKey !== undefined) out.push({ key: meta.persistKey, meta, schema });
  }
  return out;
}

export function findByGroup(group: DomainGroup): readonly z.ZodType[] {
  const out: z.ZodType[] = [];
  for (const [schema, meta] of registered()) if (meta.group === group) out.push(schema);
  return out;
}

export function findById(id: string): { schema: z.ZodType; meta: DomainMeta } | undefined {
  for (const [schema, meta] of registered()) if (meta.id === id) return { schema, meta };
  return undefined;
}

/** Emit JSON Schema for every llmExposed entry, keyed by id. */
export function toJsonSchemaCatalog(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [schema, meta] of registered()) {
    if (!meta.llmExposed) continue;
    out[meta.id] = z.toJSONSchema(schema, { metadata: domainRegistry });
  }
  return out;
}
